import { useLingui } from "@lingui/react/macro";
import styles from "./InputTextArea.module.scss";
import { useEffect } from "react";

type IInputTextArea = {
  rows?: number;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  autoResize?: boolean;
  inputTextAreaRef: React.RefObject<HTMLTextAreaElement | null>;
  placeholder?: string;
};

export const InputTextArea = ({
  rows = 1,
  value,
  onChange,
  onFocus,
  autoResize = true,
  inputTextAreaRef,
  placeholder,
}: IInputTextArea) => {
  const { t } = useLingui();
  const resolvedPlaceholder = placeholder ?? t`Skriv ett meddelande`;
  useAutosizeTextArea(autoResize, inputTextAreaRef, value);

  return (
    <textarea
      ref={inputTextAreaRef}
      className={styles.inputTextArea}
      rows={rows}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      placeholder={resolvedPlaceholder}
    />
  );
};

/**
 * Solution by https://medium.com/@oherterich/creating-a-textarea-with-dynamic-height-using-react-and-typescript-5ed2d78d9848
 *
 * Updates the height of a <textarea> when the value changes.
 * @param autoResize
 * @param textAreaRef
 * @param value
 */
const useAutosizeTextArea = (
  autoResize: boolean,
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>,
  value: string,
) => {
  useEffect(() => {
    const textAreaElement = textAreaRef.current;
    if (autoResize && textAreaElement) {
      // Setting `textAreaRef.style.height` is not React Compiler compatible.
      // We ignore this for now since there is no easy fix.
      // We need to reset the height momentarily to get the correct scrollHeight for the textarea
      textAreaElement.style.height = "0px";
      const scrollHeight = textAreaElement.scrollHeight;

      // We then set the height directly, outside of the render loop
      // Trying to set this with state or a ref will produce an incorrect value.
      textAreaElement.style.height = scrollHeight + "px";
    }
  }, [autoResize, textAreaRef, value]); // Value important for when message is sent, to shrink the textarea
};
